import React, { useRef, useState } from 'react';
import { Group, Line } from 'react-konva';

import {
    angle as calculateAngle,
    midpoint,
    getBoundingRectanglePosition,
    rotatePointsAboutPoint,
    distance,
} from 'src/util/math';
import { useDragGesture } from 'src/hooks/useDragGesture';
import { useLatest } from 'src/hooks/useLatest';

import Grabber from '../../grabber';
import type { KonvaEventObject } from 'konva/lib/Node';

type Point = {
    x: number;
    y: number;
};

const DOTTED_LINE_HEIGHT = 30;

// adding 0.01 to void any divide by 0 for calculations involving divide by sin(angle) or cos(angle)
const snapRotations = [
    0,
    Math.PI / 4 + 0.01, // 45
    Math.PI / 2 + 0.01, // 90
    (3 * Math.PI) / 4 + 0.01, // 135
    Math.PI + 0.01, // 180
    (5 * Math.PI) / 4 + 0.01, // 225
    (3 * Math.PI) / 2 + 0.01, // 270
    (7 * Math.PI) / 4 + 0.01, // 315
];

type BorderProps = {
    container: HTMLElement;
    scaledPoints: Point[];
    points: Point[];
    scale: number;
    onDragMove: (points: Point[]) => void;
    onDragEnd: () => void;
    cursorChange: (cursor: string) => void;
    offset: Point;
    rotationAngle: number;
    setRotationAngle: (angle: number) => void;
};

interface DragState {
    startPoints: Point[];
    container: HTMLElement;
    scale: number;
    offset: Point;
    rotationAngle: number;
    setRotationAngle: (angle: number) => void;
}

const getBoundingBoxWithRotation = (points: Point[], angle: number) => {
    // find center of warp
    const boundingBox = getBoundingRectanglePosition(points);
    const center = midpoint(
        { x: boundingBox.left, y: boundingBox.top },
        { x: boundingBox.right, y: boundingBox.bottom },
    );

    const rotatedPoints = rotatePointsAboutPoint(points, -angle, center.x, center.y);

    const { left, top, right, bottom } = getBoundingRectanglePosition(rotatedPoints);

    const boundingPoints = [
        { x: left, y: top },
        { x: right, y: top },
        { x: right, y: bottom },
        { x: left, y: bottom },
        { x: left, y: top }, // back to top left corner for rendering close box
    ];

    return rotatePointsAboutPoint(boundingPoints, angle, center.x, center.y);
};

const Border: React.FC<BorderProps> = ({
    container,
    scaledPoints,
    points,
    scale,
    onDragMove,
    onDragEnd,
    cursorChange,
    offset,
    rotationAngle,
    setRotationAngle,
}) => {
    const [angle, setAngle] = useState(0);
    const angleRef = useLatest(angle);

    // Pure drag bookkeeping -- never read in render -- refreshed every render so `handleDragMove`
    // (registered for the whole gesture by `useDragGesture`) always sees the latest values.
    const dragState = useRef<DragState>({
        startPoints: [],
        container,
        scale,
        offset,
        rotationAngle,
        setRotationAngle,
    });
    dragState.current.container = container;
    dragState.current.scale = scale;
    dragState.current.offset = offset;
    dragState.current.rotationAngle = rotationAngle;
    dragState.current.setRotationAngle = setRotationAngle;

    const handleDragMove = (e: MouseEvent) => {
        const {
            startPoints,
            container: currentContainer,
            scale: currentScale,
            offset: currentOffset,
            rotationAngle: currentRotationAngle,
        } = dragState.current;

        // calculate mouse position on canvas
        const containerRect = currentContainer.getBoundingClientRect();
        const mousePosition = {
            x: (e.clientX - containerRect.left - currentOffset.x) / currentScale,
            y: (e.clientY - containerRect.top - currentOffset.y) / currentScale,
        };

        // find center and top border midpoint of warp for angle calculation
        const boundingPoints = getBoundingBoxWithRotation(startPoints, currentRotationAngle);
        const topCenter = midpoint(boundingPoints[0], boundingPoints[1]);
        const center = midpoint(boundingPoints[0], boundingPoints[2]);

        let angle = calculateAngle(center, topCenter, mousePosition);

        if (distance(mousePosition, boundingPoints[3]) < distance(mousePosition, boundingPoints[2])) {
            angle = -angle;
        }

        let newAngle = (currentRotationAngle + angle) % (2 * Math.PI);
        const angleDirection = newAngle / Math.abs(newAngle) || 1;
        newAngle = Math.abs(newAngle);

        snapRotations.forEach((snapAngle) => {
            if (newAngle < snapAngle + 0.1 && newAngle > snapAngle - 0.1) {
                angle = angleDirection * (snapAngle - angleDirection * currentRotationAngle);
            }
        });

        const rotatedPoints = rotatePointsAboutPoint(startPoints, angle, center.x, center.y);

        onDragMove(rotatedPoints);
        setAngle(angle);
    };

    const { start } = useDragGesture({
        onDragMove: handleDragMove,
        onDragEnd: () => {
            onDragEnd();

            const { rotationAngle: currentRotationAngle, setRotationAngle: currentSetRotationAngle } =
                dragState.current;
            currentSetRotationAngle((currentRotationAngle + angleRef.current) % (2 * Math.PI));
            setAngle(0);
        },
    });

    const onDragStart = (e: KonvaEventObject<MouseEvent>) => {
        dragState.current.startPoints = points;
        start();
        e.cancelBubble = true;
    };

    const boundingPoints = getBoundingBoxWithRotation(scaledPoints, angle + rotationAngle);

    // dotted line from top of border line to rotation grip point
    const topMidpoint = midpoint(boundingPoints[0], boundingPoints[1]);
    let dottedPoints = [
        { x: topMidpoint.x, y: topMidpoint.y - DOTTED_LINE_HEIGHT },
        { x: topMidpoint.x, y: topMidpoint.y },
    ];

    // make dotted line perpendicular to top border line
    dottedPoints = rotatePointsAboutPoint(dottedPoints, angle + rotationAngle, topMidpoint.x, topMidpoint.y);

    // mapping out points into one long number array [x1, y1, x2, y2, ... xn, yn] to render line
    const boundingLine: number[] = [];
    boundingPoints.forEach((point) => {
        boundingLine.push(point.x);
        boundingLine.push(point.y);
    });

    const dottedLine: number[] = [];
    dottedPoints.forEach((point) => {
        dottedLine.push(point.x);
        dottedLine.push(point.y);
    });

    return (
        <Group>
            <Line points={dottedLine} stroke='rgba(0, 0, 0, .3)' dash={[4, 2]} strokeWidth={1} />
            <Line points={boundingLine} stroke='rgba(0, 0, 0, .3)' dash={[4, 2]} strokeWidth={1} />
            <Grabber position={dottedPoints[0]} cursorChange={cursorChange} onDragStart={onDragStart} />
        </Group>
    );
};

export default Border;
